/**
 * Protected Route Component
 * Wrapper for routes that require authentication
 * Supports role-based and permission-based access control
 *
 * Updated to use new 6-role hierarchy and permission system
 */

import { ReactNode } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole, getRoleDisplayName } from '@/types/auth.types'
import { evaluateRouteAccess, type RouteAccessId } from '@/security/uiAccess'
import { ShieldX, Home, LogOut, ArrowLeft } from 'lucide-react'

interface ProtectedRouteProps {
  children: ReactNode
  /** Allowed roles - user must have one of these roles */
  allowedRoles?: UserRole[]
  /** Required permission - user must have this permission */
  requiredPermission?: string
  /** Redirect path if unauthorized (default: /unauthorized) */
  redirectTo?: string
  /** Show custom unauthorized component instead of redirecting */
  fallback?: ReactNode
  routeAccess?: RouteAccessId
}

/**
 * ProtectedRoute
 * Redirects to login if not authenticated
 * Redirects to unauthorized if user lacks required role or permission
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  requiredPermission,
  redirectTo = '/403',
  fallback,
  routeAccess,
}: ProtectedRouteProps) {
  const { isLoggedIn, session, hasRole, hasPermission } = useAuth()
  const location = useLocation()

  // Show loading state while checking auth
  if (session.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-light dark:bg-surface-dark">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary-600" />
          <p className="text-text-secondary">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isLoggedIn) {
    // Store the attempted URL for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (routeAccess) {
    const decision = evaluateRouteAccess(session.user, routeAccess)
    if (!decision.allowed) {
      if (fallback) return <>{fallback}</>
      return <Navigate to={redirectTo} state={{ denialReason: decision.reason }} replace />
    }
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    if (!hasRole(allowedRoles)) {
      if (fallback) return <>{fallback}</>
      return <Navigate to={redirectTo} state={{ requiredRoles: allowedRoles }} replace />
    }
  }

  // Check permission-based access
  if (requiredPermission) {
    if (!hasPermission(requiredPermission)) {
      if (fallback) return <>{fallback}</>
      return <Navigate to={redirectTo} state={{ requiredPermission }} replace />
    }
  }

  return <>{children}</>
}

/**
 * Unauthorized Page
 * Shown when user tries to access a page they don't have permission for
 */
export function UnauthorizedPage() {
  const { user, logout } = useAuth()
  const location = useLocation()

  // Get info about what was required (if passed via state)
  const state = location.state as {
    requiredRoles?: UserRole[]
    requiredPermission?: string
  } | null

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-light to-surface-overlay dark:from-surface-dark dark:to-surface-overlay flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30">
          <ShieldX className="w-10 h-10 text-red-600 dark:text-red-400" />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-5xl font-bold text-red-600 dark:text-red-500">403</h1>
          <h2 className="text-2xl font-semibold text-text-primary">Access Denied</h2>
        </div>

        {/* Message Card */}
        <div className="card card-elevated p-6 space-y-4 text-left">
          <p className="text-text-secondary text-center">
            You don't have permission to access this page.
          </p>

          {user && (
            <div className="pt-4 border-t border-surface-overlay space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary">Logged in as</span>
                <span className="font-medium text-text-primary">{user.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary">Your role</span>
                <span className="badge badge-secondary">{getRoleDisplayName(user.role)}</span>
              </div>

              {state?.requiredRoles && (
                <div className="flex justify-between text-sm items-start">
                  <span className="text-text-tertiary">Required role</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {state.requiredRoles.map(role => (
                      <span key={role} className="badge badge-outline badge-sm">
                        {getRoleDisplayName(role)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {state?.requiredPermission && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-tertiary">Required permission</span>
                  <code className="text-xs bg-surface-overlay px-2 py-1 rounded">
                    {state.requiredPermission}
                  </code>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Link
            to={user?.authScope === 'client_vault' ? '/client-vault' : '/dashboard'}
            className="btn btn-primary gap-2"
          >
            <Home className="w-4 h-4" />
            Go to Dashboard
          </Link>
          <div className="flex gap-2">
            <button onClick={() => window.history.back()} className="btn btn-outline flex-1 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
            <button onClick={() => logout()} className="btn btn-ghost flex-1 gap-2">
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-xs text-text-tertiary">
          If you believe this is a mistake, please contact your administrator.
        </p>
      </div>
    </div>
  )
}

/**
 * RequireRole - Convenience wrapper for role-based routes
 */
export function RequireRole({
  children,
  roles,
}: {
  children: ReactNode
  roles: UserRole | UserRole[]
}) {
  const roleArray = Array.isArray(roles) ? roles : [roles]
  return <ProtectedRoute allowedRoles={roleArray}>{children}</ProtectedRoute>
}

/**
 * RequirePermission - Convenience wrapper for permission-based routes
 */
export function RequirePermission({
  children,
  permission,
}: {
  children: ReactNode
  permission: string
}) {
  return <ProtectedRoute requiredPermission={permission}>{children}</ProtectedRoute>
}

export function RequireRouteAccess({
  children,
  route,
}: {
  children: ReactNode
  route: RouteAccessId
}) {
  return <ProtectedRoute routeAccess={route}>{children}</ProtectedRoute>
}

/**
 * RequireOwnerOrAdmin - Common pattern for admin-level access
 */
export function RequireOwnerOrAdmin({ children }: { children: ReactNode }) {
  return <ProtectedRoute allowedRoles={['owner', 'admin']}>{children}</ProtectedRoute>
}

/**
 * RequireOwner - Owner-only access
 */
export function RequireOwner({ children }: { children: ReactNode }) {
  return <ProtectedRoute allowedRoles={['owner']}>{children}</ProtectedRoute>
}
